"use client"

import { useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { FieldPath, FieldValues, useWatch } from "react-hook-form"
import { cn } from "@/shared/lib/utils"
import FormSelectInput from "@/shared/components/form-inputs/form-select-input"
import { SelectInputProps } from "@/shared/components/inputs/select-input"
import WorkScopeCompletionFields from "./work-scope-completion-fields"
import { useWorkScopeCompletion } from "../hooks/use-work-scope-completion"
import { isCompletableWorkScopeStatus } from "../lib/work-scope-complete"

type WorkScopeStatusSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = SelectInputProps & {
  name: TName
  control: Parameters<typeof FormSelectInput<TFieldValues, TName>>[0]["control"]
  workScopeSlug: string
}

/**
 * Date Completed and the complete photo follow the Claimed By field, which is the
 * next grid cell. On a phone the grid is one column, so the order is status,
 * Claimed By, then the completion details. On desktop those two fields share a row
 * and the details span the row beneath them.
 */
export default function WorkScopeStatusSelect<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  workScopeSlug,
  inputGroupProps,
  name,
  control,
  ...selectProps
}: WorkScopeStatusSelectProps<TFieldValues, TName>) {
  const completion = useWorkScopeCompletion(name, workScopeSlug)
  const status = useWatch({ control, name })
  const { wrapperClassName, ...groupProps } = inputGroupProps ?? {}
  const anchorRef = useRef<HTMLDivElement>(null)
  const slotNodeRef = useRef<HTMLDivElement | null>(null)
  const [slot, setSlot] = useState<HTMLDivElement | null>(null)

  const showDetails = completion.photo != null
    || isCompletableWorkScopeStatus(typeof status === "string" ? status : null)

  useLayoutEffect(() => {
    const claimedBy = anchorRef.current?.nextElementSibling
    if (!claimedBy?.parentElement) return

    if (!slotNodeRef.current) {
      slotNodeRef.current = document.createElement("div")
      setSlot(slotNodeRef.current)
    }
    const node = slotNodeRef.current
    node.className = showDetails ? "col-span-full" : "hidden"
    if (claimedBy.nextElementSibling !== node) {
      claimedBy.after(node)
    }
  })

  useLayoutEffect(() => {
    return () => {
      slotNodeRef.current?.remove()
      slotNodeRef.current = null
    }
  }, [])

  return (
    <div className="contents" ref={anchorRef}>
      <div
        className={cn("flex flex-col gap-2", wrapperClassName)}
        data-work-scope-ready={completion.enabled ? String(completion.isReady) : undefined}
      >
        <FormSelectInput
          {...selectProps}
          name={name}
          control={control}
          inputGroupProps={groupProps}
          onUserChange={completion.onUserChange}
        />
        {completion.warning && (
          <p role="alert" className="text-xs text-destructive">
            {completion.warning}
          </p>
        )}
      </div>
      {completion.enabled && slot && createPortal(
        <WorkScopeCompletionFields
          statusFieldName={name}
          dateFieldName={completion.dateFieldName}
          photo={completion.photo}
          showDetails={showDetails}
        />,
        slot,
      )}
    </div>
  )
}
